import React, { useContext, useEffect, useState, useRef } from "react";
import { withOktaAuth as useOktaAuth } from "@okta/okta-react";
import { useNavigate, useLocation } from "react-router-dom";

import { Container, Row, Col } from "react-bootstrap";

import { ScreenHeader } from "./ScreenHeader/ScreenHeader";
import { ContentHeader } from "./ContentHeader";
import { Footer } from "./Footer";
import { NavBar } from "./NavBar";

import { UserContext } from "shared/context/UserContext";
import { AuthContext } from "shared/context/AuthContext";
import { ConstantsContext } from "shared/context/ConstantsContext";
import { getWithExpiry } from "shared/utils/localStorageAccess";
import { LiveChat } from "./LiveChat";
import { UserTypes } from "shared/constants/UserTypes";
import { OrganizationTypes } from "shared/constants/OrganizationTypes";
import { LoadingSpinner } from "./LoadingSpinner";
import { IsNullOrEmpty } from "shared/helpers/NullEmptyHelpers";

import { GetObjectPropertyValue } from "shared/helpers/DataHelpers";
import { ToSingleOrganization } from "shared/helpers/OrganizationHelpers";
import { ORGANIZATION_SELECTION_KEY } from "shared/helpers/UserSelectionCache";
import {
  ToLocationOptions,
  ToNcpdpOptions,
} from "shared/helpers/LocationOptionHelpers";

import { useFetchLayoutConstants } from "./hooks/useFetchLayoutConstants";
import { useFetchLayoutMemberConfig } from "./hooks/useFetchLayoutMemberConfig";
import { useFetchLayoutUserLocations } from "./hooks/useFetchLayoutUserLocations";
import { useFetchLayoutPayers } from "./hooks/useFetchLayoutPayers";
import { useFetchLayoutUserSubscription } from "./hooks/useFetchLayoutUserSubscription";
import { useFetchLayoutLiveChatAccess } from "./hooks/useFetchLayoutLiveChatAccess";
import { useUpdateSisenseLocations } from "./hooks/useUpdateSisenseLocations";
import { useFetchParentCompanyAttributes } from "components/hooks/useFetchParentCompanyAttributes";
import { useFetchParentCompanyAttributesForGroup } from "components/hooks/useFetchParentCompanyAttributesForGroup";

export default useOktaAuth(function Layout(props) {
  const { oktaAuth, authState } = props;

  //This will be used to ensure we are not making too many unnecessary qauthengentication checks
  const lastAuthenticationCheckTimeRef = useRef(0);
  const limitAUthenticationCheckInterval = 10000; //10 seconds

  const userConfig = useContext(UserContext);
  //const [isAuthenticated, setIsAuthenticated] = useState(null);

  const { isAuthenticated } = useContext(UserContext);
  const [user, setUser] = useState(null);
  const [showLiveChat, setShowLiveChat] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const { setConstants } = useContext(ConstantsContext);

  const { data: constantsData } = useFetchLayoutConstants(isAuthenticated);

  const { data: memberConfigData } = useFetchLayoutMemberConfig(
    isAuthenticated,
    userConfig.email,
    userConfig.contactType
  );

  const { data: userSubscriptionData } = useFetchLayoutUserSubscription(
    isAuthenticated,
    userConfig.email
  );

  const { data: livechatAccessData } = useFetchLayoutLiveChatAccess(
    isAuthenticated,
    userConfig.email
  );

  // console.log(userConfig);

  let parentCompanyId = (userConfig.currentOrganization && userConfig.currentOrganization.type === "ParentCompany")
    ? userConfig.currentOrganization.value : null;

  const {
    data: parentCompanyTparAttributes
  } = useFetchParentCompanyAttributes(parentCompanyId);

  const { data: parentCompanyGroupAttributes
  } = useFetchParentCompanyAttributesForGroup(
    userConfig.email,
    userConfig.currentOrganization && userConfig.currentOrganization.type === "Group" ? userConfig.currentOrganization.value : null,
    userConfig.currentOrganization && userConfig.currentOrganization.type === "Group" ? userConfig.currentOrganization.type : null
  );

  // if (parentCompanyTparAttributes) {
  //   console.log("Parent Company TPAR Attributes:");
  //   console.log(parentCompanyTparAttributes);

  //   //NOTE: Get the value where ABID = 94
  //   let abid94Attribute = parentCompanyTparAttributes.find(attr => attr.abid === 94);

  //   console.log(abid94Attribute);
  // }

  const { data: payersData, isLoading: payersDataLoading } =
    useFetchLayoutPayers(
      isAuthenticated,
      userConfig.email,
      GetObjectPropertyValue(userConfig.currentOrganization, "value"),
      GetObjectPropertyValue(userConfig.currentOrganization, "type"),
      location.pathname
    );

  const {
    allLocationsData,
    recLocationsData,
    metricLocationsData,
    isLoading: locationsDataLoading,
  } = useFetchLayoutUserLocations(
    isAuthenticated,
    userConfig.email,
    GetObjectPropertyValue(userConfig.currentOrganization, "value"),
    GetObjectPropertyValue(userConfig.currentOrganization, "type"),
    location.pathname
  );

  //this will be called by the locations effect and has validations that locations data has changed before issuing the call
  const { updateSisenseLocations } = useUpdateSisenseLocations(
    isAuthenticated,
    userConfig.email,
    allLocationsData
  );

  async function checkAuthentication() {
    if (isAuthenticated) {
      //if we authenticated, we need to the last time we made a call to check authentication
      // then
      const currentTime = Date.now();
      if (
        currentTime - lastAuthenticationCheckTimeRef.current <
        limitAUthenticationCheckInterval
      ) {
        return;
      }

      lastAuthenticationCheckTimeRef.current = currentTime;
    }

    if (authState) {
      const fetchedIsAuthenticated = authState.isAuthenticated;

      if (fetchedIsAuthenticated !== isAuthenticated) {
        if (authState.accessToken) {
          const userInfo = await oktaAuth.getUser();
          userConfig.onEmailChange(userInfo.email);

          setUser(userInfo);
        } else {
          setUser(null);
        }
      }

      if (fetchedIsAuthenticated === false) {
        navigate("/Login");
      }
    }
  }

  function SetPageTitle() {
    document.title = props.pageTitle == undefined ? "RME" : props.pageTitle;
  }

  useEffect(() => {
    if (!constantsData) return;

    setConstants(constantsData);
  }, [constantsData]);

  useEffect(() => {
    if (!memberConfigData) return;

    //will wrap this in a function as there is some work being done here (formatting, setting local storage, making decision if nothing selected)
    function setMemberConfig() {
      const organizations = formatOrganizationDropdownOptions(memberConfigData);

      userConfig.onOrganizationsChange(organizations);

      if (IsNullOrEmpty(userConfig.currentOrganization)) {
        //we do not have a current organization, so we need to set one, either from the localstorage or the first one in the list

        //check if we have a cached config with the last location and if so set it
        //NOTE: the cache holds a single organization, but may still hold a list written by the
        //short-lived multi-select build until that entry expires, so it is normalized first.
        const cachedOrganization = ToSingleOrganization(
          getWithExpiry(ORGANIZATION_SELECTION_KEY)
        );

        if (!IsNullOrEmpty(cachedOrganization)) {
          userConfig.onCurrentOrganizationChange(cachedOrganization);
        } else {
          //if we have no cached config, then we need to set the first organization in the list
          userConfig.onCurrentOrganizationChange(organizations[0]);
        }
      }
    }

    setMemberConfig();
  }, [memberConfigData]);

  //Loications Data
  useEffect(() => {
    if (IsNullOrEmpty(allLocationsData)) {
      // Clear all locations below if allLocationsData is empty
      userConfig.onLocationsChange([]);
      userConfig.onRecLocationsChange([]);
      userConfig.onMetricLocationsChange([]);
      userConfig.onNcpdpsChange([]);
      userConfig.onPayersChange([]);
      return;
    }

    //If we get here then we have locations data
    let allLocations = ToLocationOptions(allLocationsData);
    userConfig.onLocationsChange(allLocations);

    let allNcpdps = ToNcpdpOptions(allLocationsData);
    userConfig.onNcpdpsChange(allNcpdps);

    //make the call to update sisense
    updateSisenseLocations();

    if (!IsNullOrEmpty(recLocationsData)) {
      let recLocations = ToLocationOptions(recLocationsData);
      userConfig.onRecLocationsChange(recLocations);
    } else {
      userConfig.onRecLocationsChange([]);
    }

    if (!IsNullOrEmpty(metricLocationsData)) {
      let metricLocations = ToLocationOptions(metricLocationsData);
      userConfig.onMetricLocationsChange(metricLocations);
    } else {
      userConfig.onMetricLocationsChange([]);
    }

    if (!IsNullOrEmpty(payersData)) {
      let payers = formatDictionaryToArray(payersData);
      payers = payers.filter((x) => x.label && x.value);
      if (IsNullOrEmpty(payers)) {
        userConfig.onPayersChange([]);
        return;
      }
      let sortedPayers = payers.sort((p1, p2) =>
        p1.label > p2.label ? 1 : p1.label < p2.label ? -1 : 0
      );

      userConfig.onPayersChange(sortedPayers);
    } else {
      userConfig.onPayersChange([]);
    }
  }, [allLocationsData, recLocationsData, metricLocationsData, payersData]);

  useEffect(() => {
    if (IsNullOrEmpty(userSubscriptionData)) return;

    userConfig.onHasMetricSubscriptionChange(
      userSubscriptionData.isMetricSubscription
    );

    userConfig.onHasRecSubscriptionChange(
      userSubscriptionData.isRecSubscription
    );

    userConfig.onContactTypeChange(userSubscriptionData.contactType);
  }, [userSubscriptionData]);

  useEffect(() => {
    if (IsNullOrEmpty(livechatAccessData)) return;

    setShowLiveChat(livechatAccessData);
  }, [livechatAccessData]);

  useEffect(() => {
    checkAuthentication();
    SetPageTitle();
  });

  function formatDictionaryToArray(data) {
    return Object.keys(data).map((key) => {
      return {
        label: data[key],
        value: key,
      };
    });
  }

  function formatOrganizationDropdownOptions(data) {
    let mappedItems = [];

    if (data) {
      data.map((x) => {
        let item = {
          value: x.itemId,
          type: x.organizationType,
          labelNoSuffix: x.itemName,
          isReconArchive: x.isReconArchive,
          isGroupContainsReconArchive: x.isGroupContainsReconArchive,
          NCPDPList: x.ncpdpList,
          // Parent companies the Group's member pharmacies belong to, nested by the server on
          // Group entries only. Backs the "Decompose Groups into Parent Companies" option; these
          // stay nested and are never added to the list as top-level options.
          parentCompanies: Array.isArray(x.parentCompanies)
            ? x.parentCompanies.map((pc) => {
                return {
                  value: pc.itemId,
                  labelNoSuffix: pc.itemName,
                  isReconArchive: pc.isReconArchive,
                  NCPDPList: pc.ncpdpList,
                };
              })
            : [],
        };

        if (userConfig.contactType === UserTypes.UCT) {
          if (x.organizationType === OrganizationTypes.Group) {
            item.label = x.itemName + " - G";
          } else if (x.organizationType === OrganizationTypes.ParentCompany) {
            item.label = x.itemName + " - P";
          } else {
            item.label = x.itemName;
          }
        } else {
          item.label = x.itemName;
        }

        mappedItems.push(item);
      });

      mappedItems.sort((a, b) => {
        return a.label.localeCompare(b.label);
      });

      return mappedItems;
    }
  }

  //NOTE: This is for extracting the NCPDP from the label on the frontend. We know that NCPDPs are always 7 characters long, we know that the label always ends with ")"
  function showLoadingSpinnerWhenLocationsDataLoading() {
    //show loading spinner when Locations or Payers are loading and we have no locations or payers in the user Context

    let noLocationsOrPayers =
      IsNullOrEmpty(userConfig.locations) || IsNullOrEmpty(userConfig.payers);

    let locationsOrPayersIsLoading = locationsDataLoading || payersDataLoading;

    return noLocationsOrPayers && locationsOrPayersIsLoading;
  }

  if (oktaAuth.authenticated === null || !user) return null;

  const childrenWithProps = React.Children.map(props.children, (child) =>
    React.cloneElement(child, { passedProps: { auth: oktaAuth, user: user } })
  );

  return (
    <AuthContext.Provider value={oktaAuth}>
      <Container className="px-0 d-flex flex-column" fluid="true" id="content">
        <header>
          <ScreenHeader user={user} auth={oktaAuth}
            showTparAccountSuppressedMessage={parentCompanyTparAttributes && parentCompanyTparAttributes.find(attr => attr.abid === 94) ? true : false}
            showGroupAccountSuppressedMessage={parentCompanyGroupAttributes && parentCompanyGroupAttributes.find(attr => attr.abid === 94) ? true : false}
          />
          <ContentHeader featureTitle={props.featureTitle} />
        </header>

        <Row className="flex-fill d-flex no-gutters flex-nowrap">
          {/* <NavBar label={props.selectedNavbarLabel} /> */}
          <NavBar auth={oktaAuth} />
          <Col className="overflow-auto">
            <LoadingSpinner
              isDataLoading={showLoadingSpinnerWhenLocationsDataLoading()}
              controlsName={"layoutSpinner"}
            />
            <Row className="d-flex no-gutters flex-column" id="main-content">
              {childrenWithProps}
            </Row>
            <Footer />
            {showLiveChat ? <LiveChat /> : ""}
          </Col>
        </Row>
      </Container>
    </AuthContext.Provider>
  );
});
